import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Phone, Mail, Globe, MapPin, Home, ShoppingBag, TrendingUp } from "lucide-react";

// All cards render at a fixed 640x384 canvas so PNG export is consistent.
const CANVAS = "relative w-[640px] h-[384px] overflow-hidden";

function Qr({ url, size = 96, fg = "#0F172A", bg = "#ffffff" }) {
  if (!url) return <div style={{ width: size, height: size }} className="flex items-center justify-center rounded bg-white/80 text-[9px] text-slate-400">QR</div>;
  return <QRCodeSVG value={url} size={size} fgColor={fg} bgColor={bg} level="M" includeMargin />;
}

function Avatar({ src, name, className = "", rounded = "rounded-none" }) {
  const initials = (name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) return <img src={src} alt={name} className={`object-cover ${rounded} ${className}`} crossOrigin="anonymous" />;
  return <div className={`flex items-center justify-center bg-slate-300 font-bold text-slate-600 ${rounded} ${className}`} style={{ fontSize: 42 }}>{initials}</div>;
}

const script = { fontFamily: "'Fraunces','Georgia',serif", fontStyle: "italic" };

/* 1 — CLASSIC (light / "Homes & Co") */
function ClassicCard({ data: d }) {
  return (
    <div className={`${CANVAS} bg-[#F5F1EA]`}>
      <div className="flex h-[300px]">
        <Avatar src={d.photo} name={d.name} className="h-full w-[240px]" />
        <div className="flex flex-1 flex-col justify-center px-7">
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B08D57]">{d.brokerage || "Homes & Co"}</div>
          <h2 className="mt-2 text-4xl font-black leading-none text-[#22303C]">{d.name || "Jessica Miller"}</h2>
          <div className="mt-1 text-lg text-[#5b6b78]" style={script}>{d.title || "Real Estate Agent"}</div>
          <div className="mt-4 space-y-1 text-[12px] text-[#40515c]">
            {d.phone && <Row icon={Phone}>{d.phone}</Row>}
            {d.email && <Row icon={Mail}>{d.email}</Row>}
            {d.website && <Row icon={Globe}>{d.website}</Row>}
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex h-[84px] items-center justify-between bg-[#22303C] px-7 text-white">
        <div>
          <div className="text-sm font-bold uppercase tracking-widest">Scan to view listings</div>
          <div className="text-xs text-white/60">{d.splinkUrl || "splink.pro/jessicamiller"}</div>
        </div>
        <div className="rounded bg-white p-1"><Qr url={d.qrValue || d.splinkUrl} size={64} /></div>
      </div>
    </div>
  );
}

/* 2 — LUXURY (dark navy / gold) */
function LuxuryCard({ data: d }) {
  return (
    <div className={`${CANVAS} bg-[#0e1b2a] text-white`}>
      <div className="absolute right-0 top-0 h-full w-[42%] overflow-hidden">
        <Avatar src={d.photo} name={d.name} className="h-full w-full opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0e1b2a]" />
      </div>
      <div className="relative z-10 flex h-full flex-col justify-between p-7">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C9A24B]">{d.brokerage || "Luxury Real Estate"}</div>
          <h2 className="mt-3 text-4xl font-black leading-none">{d.name || "Michael Thompson"}</h2>
          <div className="mt-1 text-sm uppercase tracking-widest text-white/60">{d.title || "Luxury Property Specialist"}</div>
          {d.tagline && <div className="mt-3 max-w-[300px] text-sm text-white/70" style={script}>{d.tagline}</div>}
        </div>
        <div className="flex items-end gap-4">
          <div className="rounded-lg border border-[#C9A24B]/40 bg-white p-1.5"><Qr url={d.qrValue || d.splinkUrl} size={84} /></div>
          <div className="pb-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#C9A24B]">View exclusive listings</div>
            <div className="mt-1 space-y-0.5 text-[12px] text-white/80">
              {d.phone && <div>{d.phone}</div>}
              {d.email && <div>{d.email}</div>}
              {d.splinkUrl && <div className="text-[#C9A24B]">{d.splinkUrl}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 3 — CONNECT (cream / circular portrait) */
function ConnectCard({ data: d }) {
  return (
    <div className={`${CANVAS} bg-[#EFE7DA] p-7`}>
      <div className="flex h-full items-center gap-6">
        <Avatar src={d.photo} name={d.name} rounded="rounded-full" className="h-[220px] w-[220px] shrink-0 ring-8 ring-white" />
        <div className="flex-1">
          <div className="text-3xl text-[#2f6f6a]" style={script}>Let's Connect!</div>
          <h2 className="mt-1 text-3xl font-black text-[#2c3a34]">{d.name || "Sarah Lee"}</h2>
          <div className="text-sm uppercase tracking-widest text-[#7a8a80]">{d.title || "Realtor®"} · {d.brokerage || "Homes"}</div>
          <div className="mt-3 space-y-1 text-[12px] text-[#3f4f47]">
            {d.phone && <Row icon={Phone}>{d.phone}</Row>}
            {d.email && <Row icon={Mail}>{d.email}</Row>}
            {d.address && <Row icon={MapPin}>{d.address}</Row>}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-xl border-2 border-[#2f6f6a] bg-white p-2"><Qr url={d.qrValue || d.splinkUrl} size={96} fg="#2f6f6a" /></div>
          <span className="rounded-full bg-[#2f6f6a] px-3 py-1 text-[10px] font-bold text-white">{d.splinkUrl || "splink.pro/sarahlee"}</span>
        </div>
      </div>
    </div>
  );
}

/* 4 — NEIGHBORHOOD (green / friendly) */
function NeighborhoodCard({ data: d }) {
  return (
    <div className={`${CANVAS} bg-[#f2f6ee]`}>
      <div className="flex h-[292px]">
        <Avatar src={d.photo} name={d.name} className="h-full w-[250px]" />
        <div className="flex flex-1 flex-col justify-center px-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#4b7a3f]">{d.brokerage || "Greenwood Realty"}</div>
          <h2 className="mt-2 text-3xl font-black text-[#2f3d29]">{d.name || "Daniel Green"}</h2>
          <div className="text-sm text-[#5c6b54]">{d.title || "Your Neighborhood Realtor"}</div>
          <div className="mt-4 flex items-center gap-3">
            <div className="rounded-lg border border-[#4b7a3f]/30 bg-white p-1.5"><Qr url={d.qrValue || d.splinkUrl} size={78} fg="#3a6130" /></div>
            <div className="text-sm font-bold text-[#3a6130]">See homes<br />in your area →</div>
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex h-[92px] items-center justify-between bg-[#3a6130] px-7 text-white">
        <div className="space-y-0.5 text-[12px]">
          {d.phone && <div>{d.phone}</div>}
          {d.email && <div>{d.email}</div>}
        </div>
        <div className="text-right text-[12px]">
          {d.website && <div>{d.website}</div>}
          <div className="text-white/70">{d.splinkUrl || "splink.pro/danielgreen"}</div>
        </div>
      </div>
    </div>
  );
}

/* 5 — BOLD (black + cream, monogram) */
function BoldCard({ data: d }) {
  const initials = (d.name || "Lauren Richards").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`${CANVAS} flex bg-[#EDE7DE]`}>
      <div className="flex w-[46%] flex-col justify-between bg-[#141414] p-7 text-white">
        <div className="text-4xl font-black tracking-tight text-[#EDE7DE]">{initials}</div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50">{d.title || "Real Estate Agent"}</div>
          <h2 className="mt-1 text-3xl font-black leading-none">{d.name || "Lauren Richards"}</h2>
          <div className="mt-4 flex gap-3 text-[10px] uppercase tracking-widest text-white/70">
            <Chip icon={Home}>Buy</Chip><Chip icon={ShoppingBag}>Sell</Chip><Chip icon={TrendingUp}>Invest</Chip>
          </div>
        </div>
      </div>
      <div className="relative flex-1">
        <Avatar src={d.photo} name={d.name} className="absolute right-0 top-0 h-[210px] w-full" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="text-2xl font-black leading-tight text-[#141414]">Let's find <span style={script}>your</span><br />NEXT HOME</div>
          <div className="mt-3 flex items-center gap-3">
            <div className="rounded bg-white p-1 shadow"><Qr url={d.qrValue || d.splinkUrl} size={70} /></div>
            <div className="space-y-0.5 text-[11px] text-[#2b2b2b]">
              {d.phone && <div>{d.phone}</div>}
              {d.email && <div>{d.email}</div>}
              <div className="font-bold">{d.splinkUrl || "splink.pro/laurenrichards"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, children }) => <div className="flex items-center gap-2"><Icon className="h-3.5 w-3.5 opacity-70" /> {children}</div>;
const Chip = ({ icon: Icon, children }) => <span className="flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {children}</span>;

export const CARD_TEMPLATES = [
  { id: "classic", name: "Classic", Component: ClassicCard },
  { id: "luxury", name: "Luxury", Component: LuxuryCard },
  { id: "connect", name: "Let's Connect", Component: ConnectCard },
  { id: "neighborhood", name: "Neighborhood", Component: NeighborhoodCard },
  { id: "bold", name: "Bold", Component: BoldCard },
];
